// @ts-check
import { graphql } from 'gatsby';
import React from 'react';
import ActionBlock, { ActionBlock_REFERENCES } from '../blocks/ActionBlock';
import ContentBlock, { ContentBlock_REFERENCES } from '../blocks/ContentBlock';
import DownloadImagesBlock, { DownloadImagesBlock_REFERENCES } from '../blocks/DownloadImagesBlock';
import HighlightedImageBlock from '../blocks/HighlightedImageBlock';
import ProductCardSection from '../blocks/ProductCardSection';
import Accolades, { Accolades_REFERENCES } from '../components/Accolades';
import { Carousel, Carousel_REFERENCES } from '../components/Carousel';
import DocumentsNeeded from '../components/DocumentsNeeded';
import FeaturedItemsWrapper, { FeaturedItemBlock_REFERENCES } from '../components/FeaturedItemsWrapper';
import PressContact from '../components/PressContact';
import SimpleVideoBlock from '../components/SimpleVideoBlock';
import BenefitsPerks from '../temporaryComponents/BenefitsPerks';
import BlogCloud, { BlogCloud_REFERENCES } from '../temporaryComponents/BlogCloud';
import { withReferencePath } from '../utils/ReferencePaths';

export const query = graphql`
	fragment PageModularBlocksFragment on Ozk_page {
		content {
			...ContentBlockFragment
			...ProductCardSectionFragment
			...ActionBlockFragment
			...AccoladesFragment
			...FeaturedItemBlockFragment
			...HighlightedImageBlockFragment
			...DocumentsNeededFragment
			...VideoBlockFragment
			...DownloadImagesFragment
			...PressContactFragment
			...CarouselFragment
			...BenefitsPerksFragment
			...BlogCloudFragment
		}
	}
`;

const [, withPaths] = withReferencePath('content');
export const PageModularBlocks_REFERENCES = [
	...withPaths(Accolades_REFERENCES),
	...withPaths(ActionBlock_REFERENCES),
	...withPaths(Carousel_REFERENCES),
	...withPaths(ContentBlock_REFERENCES),
	...withPaths(DownloadImagesBlock_REFERENCES),
	...withPaths(FeaturedItemBlock_REFERENCES),
	...withPaths(BlogCloud_REFERENCES),
];

/**
 * @typedef ModularBlock
 * @property {import('../components/Accolades').AccoladesProps} [accolades]
 * @property {import('../blocks/ActionBlock').ActionBlockOptions} [action_block]
 * @property {{ reference: import('../blocks/ContentBlock').ContentBlockModel[] }} [content_block]
 * @property {import('../components/DocumentsNeeded').DocumentsNeededOptions} [documents_needed]
 * @property {import('../components/FeaturedItemsWrapper').FeaturedItemsOptions} [featured_item_block]
 * @property {import('../components/Carousel').CarouselOptions} [carousel]
 * @property {import('../blocks/HighlightedImageBlock').HighlightedImageBlockOptions} [image_blocks]
 * @property {import('../blocks/ProductCardSection').ProductCardBlockOptions} [product_card_grid_block]
 * @property {import('../components/SimpleVideoBlock').SimpleVideoBlockOptions} [video_block]
 * @property {import('../blocks/DownloadImagesBlock').DownloadImagesBlockOptions} [download_images]
 * @property {import('../components/PressContact').PressContactInfoOptions} [press_contact]
 * @property {import('../temporaryComponents/BenefitsPerks').BenefitsPerksOptions} [benefits_perks]
 * @property {import('../temporaryComponents/BlogCloud').BlogCloudOptions} [blog_cloud]
 *
 * @typedef PageModularBlocksProps
 * @property {ModularBlock[]} options
 */
/** @type {import('~/types/React').ReactFC<import('~/contentstack/types').ModelWithLivePreviewAttr<PageModularBlocksProps>>} */
const PageModularBlocks = ({ options }) => (
	<React.Fragment>
		{(options ?? []).map((contentReference, i) => (
			<React.Fragment key={i}>
				{contentReference?.accolades && <Accolades options={contentReference.accolades} />}

				{contentReference?.action_block && <ActionBlock options={contentReference.action_block} key={i} />}

				{contentReference?.content_block?.reference.map((blockModel, j) => (
					<ContentBlock model={blockModel} key={j} />
				))}

				{contentReference?.documents_needed && <DocumentsNeeded options={contentReference.documents_needed} />}

				{contentReference?.featured_item_block && (
					<FeaturedItemsWrapper options={contentReference.featured_item_block} key={i} />
				)}

				{contentReference?.image_blocks && <HighlightedImageBlock options={contentReference.image_blocks} />}

				{contentReference?.product_card_grid_block && (
					<ProductCardSection options={contentReference.product_card_grid_block} />
				)}

				{contentReference?.video_block && <SimpleVideoBlock options={contentReference.video_block} />}

				{contentReference?.download_images && <DownloadImagesBlock options={contentReference.download_images} />}

				{contentReference?.press_contact && <PressContact options={contentReference.press_contact} />}

				{contentReference?.carousel && <Carousel options={contentReference.carousel} />}

				{contentReference?.benefits_perks && <BenefitsPerks options={contentReference.benefits_perks} />}

				{contentReference?.blog_cloud && <BlogCloud options={contentReference.blog_cloud} />}
			</React.Fragment>
		))}
	</React.Fragment>
);

export default PageModularBlocks;
